import { DocTypeKey } from 'types';
import { getPrettyFormattedUtcDate } from 'utils/dates';
import { DocumentKey } from 'containers/worker/utils';
import {
  buildOptionsWithCurrentValue,
  citizenshipStatusOptions,
  workAuthorizationStatusOptions,
} from 'containers/worker/helpers/utils';
import { getJobsiteContractorsRadioGroupOptions } from 'containers/contractor/helpers';
import { toFancySelectOptions } from 'utils/constants';
import {
  SelfOnboardingState,
  SelfOnboardingUser,
  SelfOnboardingWorker,
  JobsiteInvitation,
  Jobsite,
  LanguageOption,
  SelfOnboardingDocumentStep,
} from './types';
import {
  statesOptions,
  primaryLanguageOptions,
  genderOptions,
  raceOptions,
  veteranOptions,
  tradeExperienceOptions,
} from './steps/profileStep/ProfileStep.forms';
import { emergencyContactRelationshipOptions } from './steps/emergencyContactStep/EmergencyContactStep.forms';
import {
  getValuesFromAdditionalFields,
  resolveDocumentInfo,
  stepDocumentKeys,
} from './steps/documentStep/DocumentStep.forms';
import {
  getFeaturesModule,
  getMultiInstanceDocumentTypesByWorkerDocType,
  getVisibleDocuments,
} from './helpers/utils';

const getBasicInfoFormData = (
  user: SelfOnboardingUser,
): SelfOnboardingState['basicInfo'] => {
  const { identity, worker } = user ?? {};
  const { firstName, lastName, email, phoneNumber } = identity ?? {};
  const { middleInitial, suffix, birthDate, ssnLastFour } = worker ?? {};

  const document = worker?.documents.edges.find(
    ({ node }) =>
      node.jobsiteWorkerDocumentType.workerDocumentType.key ===
      DocumentKey.WorkerConsentDocument,
  )?.node;

  const { additionalFieldValues } = document?.latestVersion ?? {};
  const valuesFromAdditionalFields = getValuesFromAdditionalFields(
    DocumentKey.WorkerConsentDocument,
    additionalFieldValues,
  );

  return {
    firstName,
    lastName,
    email,
    phoneNumber,
    middleInitial,
    suffix,
    birthDate: getPrettyFormattedUtcDate(birthDate),
    ssnLastFour,
    workerConsentStatus: valuesFromAdditionalFields.acknowledgmentStatus,
  };
};

const getProfileFormData = (args: {
  worker: SelfOnboardingWorker;
  jobsiteWorker: SelfOnboardingState['jobsiteWorker'];
  tradeOptions: SelfOnboardingState['tradeOptions'];
  jobTitleOptions: SelfOnboardingState['jobTitleOptions'];
  unionAffiliationOptions: SelfOnboardingState['unionAffiliationOptions'];
  classificationGroupOptions: SelfOnboardingState['classificationGroupOptions'];
}): SelfOnboardingState['profile'] => {
  const {
    worker,
    jobsiteWorker,
    tradeOptions,
    jobTitleOptions,
    unionAffiliationOptions,
    classificationGroupOptions,
  } = args;

  const {
    addressLine1,
    addressLine2,
    addressCity,
    addressState,
    addressZipCode,
    primaryLanguage,
    citizenshipStatus,
    workAuthorizationStatus,
    race,
    gender,
    isVeteran,
  } = worker ?? {};

  const {
    trade: currentTrade,
    jobTitle: currentJobTitle,
    classificationGroup,
    unionAffiliation,
    tradeExperience,
  } = jobsiteWorker ?? {};

  return {
    addressLine1,
    addressLine2,
    addressCity,
    addressState: statesOptions.find((opt) => opt.value === addressState),
    addressZipCode,
    primaryLanguage: primaryLanguageOptions.find(
      (opt) => opt.value === primaryLanguage,
    ),
    citizenshipStatus: citizenshipStatusOptions.find(
      (opt) => opt.value === citizenshipStatus,
    ),
    workAuthorizationStatus: workAuthorizationStatusOptions.find(
      (opt) => opt.value === workAuthorizationStatus,
    ),
    race: raceOptions.find((opt) => opt.value === race),
    gender: genderOptions.find((opt) => opt.value === gender),
    isVeteran: veteranOptions[isVeteran ? 0 : 1],
    trade: tradeOptions.find((opt) => opt.value === currentTrade),
    tradeExperience: tradeExperienceOptions.find(
      (opt) => opt.value === tradeExperience,
    ),
    jobTitle: jobTitleOptions.find((opt) => opt.value === currentJobTitle),
    unionAffiliation: unionAffiliationOptions.find(
      (opt) => opt.value === unionAffiliation,
    ),
    classificationGroup: classificationGroupOptions.find(
      (opt) => opt.value === classificationGroup,
    ),
  };
};

const getEmergencyContactFormData = (
  worker: SelfOnboardingWorker,
): SelfOnboardingState['emergencyContact'] => {
  const {
    emergencyContactName,
    emergencyContactRelationship,
    emergencyContactPhone,
  } = worker ?? {};

  return {
    emergencyContactName,
    emergencyContactRelationship: emergencyContactRelationshipOptions.find(
      (opt) => opt.value === emergencyContactRelationship,
    ),
    emergencyContactPhone,
  };
};

const getDocumentTypes = (args: {
  jobsite: Jobsite;
  language: LanguageOption;
}): SelfOnboardingState['documentTypes'] => {
  const { jobsite, language } = args;
  return Object.fromEntries(
    jobsite.documentTypes.edges.map(({ node: dt }) => {
      const acknowledgmentFile =
        dt.files?.find((f) => f.language === language.value) ??
        dt.files?.find((f) => f.language === 'en');

      const title = acknowledgmentFile?.title ?? dt.name;
      const isMultiInstance = !!dt.key;

      return [
        dt.key ?? dt.workerDocumentType.key,
        {
          id: dt.id,
          acknowledgmentFile,
          title,
          isMultiInstance,
          multiInstanceDocTitle: isMultiInstance ? title : null,
        },
      ];
    }),
  );
};

const getDocumentSteps = (args: {
  jobsite: Jobsite;
}): SelfOnboardingDocumentStep[] => {
  const { jobsite } = args;
  const multiInstanceDocumentTypesByWorkerDocType =
    getMultiInstanceDocumentTypesByWorkerDocType(jobsite);
  const visibleDocuments = getVisibleDocuments(jobsite.modules);

  return stepDocumentKeys.flatMap(
    (stepKey): SelfOnboardingDocumentStep | SelfOnboardingDocumentStep[] => {
      const multiInstanceDocTypes =
        multiInstanceDocumentTypesByWorkerDocType[stepKey];
      if (multiInstanceDocTypes) {
        return multiInstanceDocTypes
          .filter((dt) => visibleDocuments?.includes(dt.key))
          .map(
            (dt): SelfOnboardingDocumentStep => ({
              key: dt.key as DocTypeKey,
              workerDocTypeKey: stepKey,
            }),
          );
      }
      if (visibleDocuments?.includes(stepKey)) {
        return {
          key: stepKey as DocTypeKey,
          workerDocTypeKey: stepKey,
        };
      }
      // .flatMap() automatically filters out empty arrays,
      return [];
    },
  );
};

const getDocumentsFormData = (args: {
  worker: SelfOnboardingWorker;
  jobsite: Jobsite;
  language: LanguageOption;
}): SelfOnboardingState['documents'] => {
  const { worker, jobsite, language } = args;
  const documentTypes = jobsite && getDocumentTypes({ jobsite, language });
  const documentSteps = jobsite && getDocumentSteps({ jobsite });
  return worker?.documents.edges
    .map(({ node: document }): SelfOnboardingState['documents'][number] => {
      const { jobsiteWorkerDocumentType: jwdt } = document;
      const documentStepKey = jwdt.key ?? jwdt.workerDocumentType.key;
      const documentInfo = resolveDocumentInfo(documentStepKey, documentSteps);
      const { key: documentKey, workerDocTypeKey } = documentInfo;
      if (!documentKey) return null;

      const { jobsiteWorkerDocumentVersionId, files, additionalFieldValues } =
        document.latestVersion;
      const valuesFromAdditionalFields = getValuesFromAdditionalFields(
        workerDocTypeKey,
        additionalFieldValues,
      );

      const documentType = documentTypes[documentKey];

      return {
        versionId: jobsiteWorkerDocumentVersionId,
        key: documentKey,
        workerDocTypeKey,
        acknowledgmentFile: documentType?.acknowledgmentFile,
        documentType,
        frontFile: files?.[0],
        backFile: files?.[1],
        ...valuesFromAdditionalFields,
      };
    })
    .filter(Boolean);
};

type GetSelfOnboardingStateArgs = {
  user: SelfOnboardingUser;
  jobsiteInvitation: JobsiteInvitation;
  contractorId: string;
  language: LanguageOption;
};

export const getSelfOnboardingLanguageDependentState = (
  args: Omit<GetSelfOnboardingStateArgs, 'contractorId'>,
): Pick<SelfOnboardingState, 'documentTypes' | 'documents'> => {
  const { jobsiteInvitation, language, user = null } = args;
  const { jobsite = null } = jobsiteInvitation?.jobsiteContractor ?? {};
  const { worker = null } = user ?? {};

  return {
    documentTypes: jobsite && getDocumentTypes({ jobsite, language }),
    documents: jobsite && getDocumentsFormData({ worker, jobsite, language }),
  };
};

export const getSelfOnboardingState = (
  args: GetSelfOnboardingStateArgs,
): SelfOnboardingState => {
  const { jobsiteInvitation = null, user = null, contractorId = null } = args;
  const { jobsite, contractor } = jobsiteInvitation?.jobsiteContractor ?? {};
  const { worker = null, userAccountId = null } = user ?? {};
  const {
    workerId = null,
    quickCode = null,
    jobsiteWorkers,
    profilePicture = null,
    editableFields,
    selfOnboardingCurrentStep = null,
    isAssignedToMultipleJobsites = null,
  } = worker ?? {};
  const { node: jobsiteWorker = null } =
    jobsiteWorkers?.edges?.find(
      ({ node }) =>
        node.jobsiteContractor.jobsite.jobsiteId === jobsite.jobsiteId,
    ) ?? {};
  const tradeOptions = toFancySelectOptions(
    buildOptionsWithCurrentValue(jobsite.tradeOptions, jobsiteWorker?.trade),
  );
  const jobTitleOptions = toFancySelectOptions(
    buildOptionsWithCurrentValue(jobsite.titleOptions, jobsiteWorker?.jobTitle),
  );

  const unionAffiliationOptions = toFancySelectOptions(
    buildOptionsWithCurrentValue(
      jobsite.unionAffiliationOptions,
      jobsiteWorker?.unionAffiliation,
    ),
  );

  const classificationGroupOptions = toFancySelectOptions(
    buildOptionsWithCurrentValue(
      jobsite.classificationGroupOptions,
      jobsiteWorker?.classificationGroup,
    ),
  );

  const jobsiteContractors = jobsite?.jobsiteContractors.edges.map(
    ({ node }) => node,
  );
  const contractorOptions =
    getJobsiteContractorsRadioGroupOptions(jobsiteContractors);

  const documentSteps = jobsite && getDocumentSteps({ jobsite });

  const jobsiteCustomWorkerDataFields =
    jobsite?.jobsiteCustomWorkerDataFields?.edges?.map(({ node }) => ({
      ...node,
      validationMessage: node.validationMessage ?? null,
      validationRegex: node.validationRegex ?? null,
    })) ?? [];

  return {
    matchedWorker: null,
    tradeOptions,
    jobTitleOptions,
    unionAffiliationOptions,
    classificationGroupOptions,
    jobsiteCustomWorkerDataFields,
    worker: worker && {
      workerId,
      quickCode,
      editableFields,
      selfOnboardingCurrentStep,
      isAssignedToMultipleJobsites,
    },
    jobsiteWorker,
    userAccountId,
    didWorkerMatch: false,
    shouldSelectContractor: Boolean(
      contractor?.isDefaultContractor &&
        getFeaturesModule(jobsite?.modules)
          ?.enforceValidContractorInSelfOnboarding,
    ),
    contractorOptions,
    contractorId:
      contractorOptions?.find((opt) => opt.value === contractorId) ?? null,
    basicInfo: user && getBasicInfoFormData(user),
    profile:
      worker &&
      getProfileFormData({
        worker,
        jobsiteWorker,
        tradeOptions,
        jobTitleOptions,
        unionAffiliationOptions,
        classificationGroupOptions,
      }),
    emergencyContact: worker && getEmergencyContactFormData(worker),
    profilePhoto: { file: profilePicture },
    verificationPhoneNumber: null,
    documentSteps,
    ...getSelfOnboardingLanguageDependentState(args),
  };
};
